/**
 * Friendly error printing for the CLI.
 *
 * Replaces the bare `console.error((err as Error).message)` pattern with
 * a consistent symbol, color, and — when we recognize the error — a hint
 * for what the user should do next.
 */
import chalk from "chalk";

interface Hint {
  pattern: RegExp;
  hint: (msg: string) => string;
}

const HINTS: Hint[] = [
  {
    pattern: /no cai scaffold|no \.cai|cannot find scaffold|no\s+ROUTER\.md/i,
    hint: () => `Run ${chalk.white("cai setup")} to bootstrap the scaffold in this project.`,
  },
  {
    pattern: /no git repository|not a git repository|fatal:\s+not a git/i,
    hint: () => `This command needs a git repository. Run ${chalk.white("git init")} first.`,
  },
  {
    pattern: /git: command not found|spawn git enoent/i,
    hint: () => `git is not installed or not in your PATH.`,
  },
  {
    pattern: /pattern .* already exists/i,
    hint: () => `Use a different name, or delete the existing pattern file.`,
  },
  {
    pattern: /pattern file not found|no matching pattern/i,
    hint: () => `Run ${chalk.white("cai pattern library")} to see what's available.`,
  },
  {
    pattern: /no cluster with id|no recorded prompts|no recurring/i,
    hint: () => `Run ${chalk.white("cai learn enable")} and use Claude for a few days, then try again.`,
  },
  {
    pattern: /CLAUDE\.md not found/i,
    hint: () => `Run ${chalk.white("cai setup")} to generate CLAUDE.md.`,
  },
  {
    pattern: /EACCES|permission denied/i,
    hint: () => `Permission denied. Check that you can write to this directory.`,
  },
  {
    pattern: /ENOSPC/i,
    hint: () => `No space left on device.`,
  },
];

/** Print a CLI error and exit. Recognizes common errors and adds a helpful hint. */
export function printError(err: unknown): void {
  const message = err instanceof Error ? err.message : String(err);
  const cleaned = message.replace(/^Error:\s*/i, "");

  console.error(`${chalk.red("✖")} ${cleaned}`);

  for (const { pattern, hint } of HINTS) {
    if (pattern.test(cleaned)) {
      console.error(`  ${chalk.dim("→")} ${chalk.dim(hint(cleaned))}`);
      return;
    }
  }
}

/** Wrap a CLI action so any thrown error is printed nicely and exits 1. */
export function withErrorHandling<T extends unknown[]>(
  action: (...args: T) => Promise<void> | void
): (...args: T) => Promise<void> {
  return async (...args: T) => {
    try {
      await action(...args);
    } catch (err) {
      printError(err);
      process.exit(1);
    }
  };
}
